import type { ComposerValues } from "./components/Composer";
import type { ClipComposerValues } from "./components/ClipComposer";

import { useMemo, useSyncExternalStore } from "react";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import { canAuthorCoachFeedback } from "@/features/coach/coachWorkspaceAccess";

const KEY_PREFIX = "tktic_cr_draft";
let discardGeneration = 0;
let cleanupPending = false;
const CLEANUP_KEY = `${KEY_PREFIX}:discard-pending`;

/** Retry failed explicit-discard cleanup before exposing any fresh partition.
 * The marker survives reload whenever storage allowed recording the discard. */
function draftStorageReady(): boolean {
  try {
    cleanupPending ||= sessionStorage.getItem(CLEANUP_KEY) !== null;
    if (!cleanupPending) return true;
    const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i));
    for (const key of keys) {
      if (key?.startsWith(`${KEY_PREFIX}:`) && key !== CLEANUP_KEY) {
        sessionStorage.removeItem(key);
        if (sessionStorage.getItem(key) !== null) return false;
      }
    }
    sessionStorage.removeItem(CLEANUP_KEY);
    if (sessionStorage.getItem(CLEANUP_KEY) !== null) return false;
    cleanupPending = false;
    return true;
  } catch { return false; }
}
const discardListeners = new Set<() => void>();
function subscribeToDiscard(listener: () => void): () => void {
  discardListeners.add(listener);
  return () => { discardListeners.delete(listener); };
}
function currentDiscardGeneration(): number { return discardGeneration; }

/** Local plaintext, tab-scoped manual work. This partitions restoration; it
 * does not encrypt storage or protect against scripts/device access. Explicit
 * logout discards it. Expiry locks the UI but retains the author's partition
 * for fresh authorized reauthentication. Unowned legacy drafts are never read. */
export type DraftContext = Readonly<{
  actorId: string;
  clubId: string;
  teamId: string;
  seasonId: string | null;
  generation: number;
}>;

/** Capture once for a composer lifetime, never from a later cleanup's stores. */
export function captureDraftContext(
  context: Omit<DraftContext, "generation">,
): DraftContext {
  return Object.freeze({ ...context, generation: discardGeneration });
}

/** Read only a coherent, materialized, currently authorized /me context.
 * Auth and scope are separate stores reconciled sequentially; wait until both
 * agree rather than briefly assigning the previous team's form to a new actor. */
function useDraftContext(requireAuthor: boolean): DraftContext | null {
  // A discard remains observable even if logout + same-account login are
  // batched into a render whose final identity/scope matches its old values.
  const generation = useSyncExternalStore(subscribeToDiscard, currentDiscardGeneration, currentDiscardGeneration);
  const me = useAuthStore((s) => s.me);
  const hydrating = useAuthStore((s) => s.hydrating);
  const teamId = useScopeStore((s) => s.teamId);
  const seasonId = useScopeStore((s) => s.seasonId);
  const selectionRequired = useScopeStore((s) => s.selectionRequired);
  const actorId = me?.user?.id;
  const clubId = me?.current_club?.id;
  const authorized = !hydrating && !selectionRequired &&
    (!requireAuthor || canAuthorCoachFeedback(me)) && !!actorId && !!clubId && !!teamId &&
    me?.active_scope?.team.id === teamId &&
    (me?.active_scope?.season?.id ?? null) === seasonId;
  return useMemo(() => authorized && actorId && clubId && teamId
    ? captureDraftContext({ actorId, clubId, teamId, seasonId })
    : null, [authorized, actorId, clubId, teamId, seasonId, generation]);
}

/** React identity includes logout generation so old closures cannot revive work. */
export function draftContextKey(context: DraftContext | null): string {
  return JSON.stringify(context ? [context.actorId, context.clubId,
    context.teamId, context.seasonId, context.generation] : null);
}

export function isCurrentScopedIdentity(context: DraftContext | null): boolean {
  if (!context || context.generation !== discardGeneration) return false;
  const { me, hydrating } = useAuthStore.getState();
  const scope = useScopeStore.getState();
  return !hydrating && !scope.selectionRequired &&
    me?.user?.id === context.actorId && me?.current_club?.id === context.clubId &&
    me?.active_scope?.team.id === context.teamId &&
    (me?.active_scope?.season?.id ?? null) === context.seasonId &&
    scope.teamId === context.teamId && scope.seasonId === context.seasonId;
}

/** Same identity/generation boundary for ordinary authenticated viewer links. */
export function useScopedIdentityContext(): DraftContext | null { return useDraftContext(false); }
export function useComposerDraftContext(): DraftContext | null { return useDraftContext(true); }
export function isCurrentDraftContext(context:DraftContext|null): boolean {
  return isCurrentScopedIdentity(context) && canAuthorCoachFeedback(useAuthStore.getState().me);
}

/** Canonical continuation shares the existing manual-work logout/expiry
 * policy. It owns a separate closed decoder; legacy partial drafts never enter
 * the canonical command stream. Only fresh coherent author authority can read. */
export function feedbackContinuationPrefix(context: DraftContext): string | null {
  return isCurrentDraftContext(context) && draftStorageReady()
    ? `${KEY_PREFIX}:feedback-v1:${JSON.stringify([context.actorId, context.clubId, context.teamId, context.seasonId])}:`
    : null;
}

/** Separate Resource command journal, sharing manual-work logout/expiry cleanup. */
export function resourceContinuationPrefix(context: DraftContext): string | null {
  return isCurrentDraftContext(context) && draftStorageReady()
    ? `${KEY_PREFIX}:resource-v1:${JSON.stringify([context.actorId, context.clubId, context.teamId, context.seasonId])}:`
    : null;
}

export type DraftKind = "note" | "clip";

/** A persisted draft is a partial of whichever composer's values —
 * partial because we snapshot live RHF state that may be mid-edit. */
export type NoteDraft = Partial<ComposerValues>;
export type ClipDraft = Partial<ClipComposerValues>;
export type ComposerDraft = NoteDraft | ClipDraft;

/** Note/clip and edit target are additional partitions of the fixed context. */
export type DraftScope = DraftContext & {
  kind: DraftKind;
  editId?: string | null;
};

function draftKey(scope: DraftScope | null): string | null {
  if (!scope || !scope.actorId || !scope.clubId || !scope.teamId ||
      scope.seasonId === undefined || scope.generation !== discardGeneration || !draftStorageReady())
    return null;
  return `${KEY_PREFIX}:v2:${JSON.stringify([scope.actorId, scope.clubId,
    scope.teamId, scope.seasonId, scope.kind, scope.editId ?? null])}`;
}

/** Explicit discard happens before identity clears. Advancing the generation
 * also rejects unmount/pagehide callbacks already holding private form values.
 * Other sessionStorage namespaces remain intact; never inspect their contents. */
export function discardComposerDrafts(): void {
  discardGeneration += 1;
  cleanupPending = true;
  try { sessionStorage.setItem(CLEANUP_KEY, '1'); } catch { /* in-memory fence remains */ }
  draftStorageReady();
  // Fresh mounts remain blocked until any failed cleanup succeeds.
  discardListeners.forEach((listener) => listener());
}

/** Treat an all-empty / whitespace-only draft as "no draft" — we
 * don't want an untouched composer to leave a phantom draft that
 * re-seeds an empty override and defeats the computed defaults. A
 * draft counts as meaningful if any string field has non-whitespace
 * content, any player is tagged, or a clip carries finite timing bounds.
 * Callers gate untouched forms with RHF dirty state before saving. */
function isMeaningfulDraft(draft: ComposerDraft): boolean {
  for (const [key, value] of Object.entries(draft)) {
    if (typeof value === "string" && value.trim() !== "") return true;
    if (Array.isArray(value) && value.length > 0) return true;
    if (
      (key === "start_seconds" || key === "end_seconds") &&
      typeof value === "number" &&
      Number.isFinite(value)
    ) {
      return true;
    }
  }
  return false;
}

/** Persist a draft for the given scope. A non-meaningful (empty)
 * draft clears any existing stored draft instead of writing a
 * phantom — symmetric with `loadDraft` returning null for those. */
export function saveDraft(scope: DraftScope | null, draft: ComposerDraft): void {
  try {
    const key = draftKey(scope);
    if (!key) return;
    if (!isMeaningfulDraft(draft)) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // private mode / quota — no-op.
  }
}

/** Load a draft for the given scope, or null if none is stored or
 * the stored value is malformed. Returns the parsed partial as-is;
 * the caller merges it into the composer override. */
export function loadDraft(scope: DraftScope | null): ComposerDraft | null {
  try {
    const key = draftKey(scope);
    if (!key) return null;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return null;
    if (!isMeaningfulDraft(parsed as ComposerDraft)) return null;
    return parsed as ComposerDraft;
  } catch {
    return null;
  }
}

/** Remove a stored draft for the given scope. Called on a successful
 * save and on explicit clip-mode exit (treated as cancel). */
export function clearDraft(scope: DraftScope | null): void {
  try {
    const key = draftKey(scope);
    if (key) sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}
